<template>
  <v-row class="breadcrumb">
    <v-col v-if="currentSelectedProject" class="pl-0 mb-0 project-overview">
      <v-btn
        variant="plain"
        data-testid="project-overview-btn"
        class="project-btn"
        @click="goToProjectConfig"
      >
        <span class="selected-project">{{ currentSelectedProject?.name }}</span>
      </v-btn>
      <span class="separator"> / </span>
    </v-col>

    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="center"
      class="choose-menu"
      transition="scale-transition"
    >
      <template #activator="{ props }">
        <v-col v-if="currentSnapshot" class="pl-1 pr-0 mb-0 breadcrumb-item-selector">
          <v-btn data-testid="snapshot-selection-btn" variant="plain" v-bind="props" class="mb-1">
            {{ currentSnapshot.name + ' (' + formatDateNoTime(currentSnapshot.date) + ')' }}
            <v-icon
              :icon="
                !menu ? CONSTANTS.ICONS_MDI.ICON_CHEVRON_DOWN : CONSTANTS.ICONS_MDI.ICON_CHEVRON_UP
              "
            />
          </v-btn>
        </v-col>
      </template>

      <SnapshotSearch
        :current-snapshot="currentSnapshot"
        class="select-items-menu"
        @close-snapshot-menu="menu = false"
      />
    </v-menu>

    <v-col v-if="currentSnapshot" class="pl-0 mb-0 project-overview">
      <span class="separator"> / </span>
    </v-col>

    <v-menu
      v-if="currentSnapshot"
      v-model="filterMenu"
      :close-on-content-click="false"
      location="center"
      class="choose-menu"
      transition="scale-transition"
    >
      <template #activator="{ props }">
        <v-col class="pl-1 mb-0 breadcrumb-item-selector">
          <v-btn data-testid="filter-selection-btn" variant="plain" v-bind="props" class="mb-1">
            <template v-if="selectedGlobalFilter">
              {{ selectedGlobalFilter.name }}
            </template>
            <template v-else>
              {{ labels.GLOBAL_FILTER.NO_FILTER_APPLIED }}
            </template>

            <v-icon
              :icon="
                !filterMenu
                  ? CONSTANTS.ICONS_MDI.ICON_CHEVRON_DOWN
                  : CONSTANTS.ICONS_MDI.ICON_CHEVRON_UP
              "
            />
          </v-btn>
        </v-col>
      </template>

      <HeaderFilters
        :current-global-filter="selectedGlobalFilter"
        class="select-items-menu"
        @close-filter-menu="filterMenu = false"
      />
    </v-menu>
  </v-row>
</template>

<script lang="ts" setup>
  import { GlobalFilter } from '@/models'
  import { useProjectStore } from '@/stores/projectStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { ref, watch } from 'vue'
  import labels from '@/translations/labels.json'
  import { useRouter } from 'vue-router'
  import { formatDateNoTime } from '@/utils/helpers'
  import SnapshotSearch from '@/components/SnapshotSearch/SnapshotSearch.vue'
  import CONSTANTS from '@/constants/Config.constants'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import HeaderFilters from '../HeaderFilters/HeaderFilters.vue'
  import { EventBus } from '@/event-bus'
  import { storeToRefs } from 'pinia'

  const snapshotStore = useSnapshotStore()
  const projectStore = useProjectStore()
  const globalFilterStore = useGlobalFilterStore()

  const router = useRouter()
  const menu = ref(false)
  const filterMenu = ref(false)

  const { currentSnapshot } = storeToRefs(snapshotStore)
  const { currentProject: currentSelectedProject } = storeToRefs(projectStore)
  const { selectedGlobalFilter } = storeToRefs(globalFilterStore)

  watch(selectedGlobalFilter, (filter: GlobalFilter | undefined) => {
    // Add or remove 'filter' query parameter
    const query = filter
      ? { filter: filter.id }
      : { ...router.currentRoute.value.query, filter: undefined }

    router.push({ query })

    const message = filter
      ? `Filter "${filter.name}" is selected!`
      : `"${labels.GLOBAL_FILTER.NO_FILTER_APPLIED}" is selected!`

    EventBus.emit('showTopAlertMessage', {
      type: 'success',
      message: message,
      isShown: true,
    })
  })

  function goToProjectConfig(): void {
    router.push({
      name: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
      params: {
        projectId: currentSelectedProject?.value?.id,
      },
    })
  }
</script>

<style lang="scss" scoped>
  .choose-menu {
    z-index: 4000 !important;
  }

  .breadcrumb-item-selector {
    margin-top: 6px;
    margin-left: -18px;
    color: #1a1a1a;
  }

  .separator {
    position: fixed;
    top: 15px;
    bottom: 15px;
    margin-left: -4px;
    margin-right: 0px;
    color: #1a1a1a;
  }

  .select-items-menu {
    position: absolute;
    top: 1.8em;
    right: -11.5em;
    min-width: 380px;
    max-width: 380px;
  }

  .selected-project {
    font-weight: bold;
    color: #1a1a1a;
    padding-right: 2px;
    text-transform: capitalize;
  }

  .project-overview {
    margin-top: 6px;
    max-width: fit-content;
  }
</style>
